import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApiClient } from "../lib/api-context.js";
import { ExpenseTreatmentQuestion } from "./ExpenseTreatmentQuestion.js";
import { classificationLabel, type ExpenseAllocation } from "../lib/overhead-review.js";

interface EvidenceDimension {
  state: "present" | "unassigned" | "not_configured" | "unknown" | "missing_id";
  id: string | null;
}
export interface ExpenseEvidenceGroup {
  id: string;
  name: string;
  section: string | null;
  amount: number | null;
  postingAmount: number;
  difference: number | null;
  lineCount: number;
  reconciled: boolean;
  reviewedAllocations?: ExpenseAllocation[];
  lines: Array<{
    evidenceId: string;
    transactionId: string;
    transactionType: string;
    transactionDate: string;
    amount: number;
    class: EvidenceDimension;
    location: EvidenceDimension;
  }>;
}
export interface AccountingExpenseEvidenceView {
  status: "not_connected" | "reconnect" | "not_ready" | "stale" | "ready" | "needs_attention" | "unsupported";
  source: {
    snapshotId: string;
    companyName: string;
    periodStart: string;
    periodEnd: string;
    currency: string;
    reportBasis: string;
    fetchedAt: string;
    reasons: string[];
    limitations: string[];
  } | null;
  groups: ExpenseEvidenceGroup[];
}

export function AccountingExpenseEvidence({ canEdit = false }: { canEdit?: boolean }): React.ReactElement | null {
  const api = useApiClient();
  const query = useQuery({
    queryKey: ["overhead-review", "expense-evidence"],
    queryFn: () => api.get<AccountingExpenseEvidenceView>("/business-costs/overhead/review/evidence"),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  // Other accounting providers retain their existing review experience.
  if (query.data?.status === "not_connected" && !query.isError) return null;
  return <AccountingExpenseEvidenceCard data={query.data} loading={query.isLoading} failed={query.isError} onRetry={() => void query.refetch()} canEdit={canEdit} />;
}

export function expenseEvidenceStatus(data: AccountingExpenseEvidenceView | undefined): { title: string; detail: string; checked: boolean } {
  const checked = data?.status === "ready" && data.source !== null && data.groups.every((group) => group.reconciled);
  if (checked && data.groups.length === 0) return { title: "No expenses found", detail: "QuickBooks reported no expenses for this month, so there is nothing to review.", checked: true };
  if (checked) return { title: "Source totals match", detail: "The saved expense entries match the account totals in QuickBooks for this month.", checked: true };
  switch (data?.status) {
    case "not_connected": return { title: "QuickBooks is not connected", detail: "Connect the right company before checking its expense details.", checked: false };
    case "reconnect": return { title: "QuickBooks needs to be reconnected", detail: "The saved connection is not active. Reconnect it on the Connections page.", checked: false };
    case "stale": return { title: "These saved details are out of date", detail: "The company connection or source report changed. Refresh overhead before relying on this detail.", checked: false };
    case "not_ready": return { title: "Expense details are not ready yet", detail: "No current expense-detail check is saved. Use Refresh overhead on the QuickBooks connection card.", checked: false };
    case "unsupported": return { title: "This report cannot be checked yet", detail: "The report uses details this check does not support. These amounts are not cleared for review.", checked: false };
    case "ready":
    case "needs_attention": return { title: "Source details need attention", detail: "Some entries are incomplete or do not match the saved expense totals. The source check is not complete.", checked: false };
    default: return { title: "Expense details are unavailable", detail: "Refresh this check to load the latest saved details. Nothing has been approved.", checked: false };
  }
}

function dateLabel(value: string): string {
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" })
    : "Date unavailable";
}
function money(value: number | null, currency: string): string {
  if (value === null || !Number.isFinite(value)) return "Not available";
  return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}
function sourceReason(reason: string): string {
  if (/currency/.test(reason)) return "The currency is missing, different, or unsupported for this check.";
  if (/basis/.test(reason)) return "The report must count costs when recorded as expenses, not when cash is paid.";
  if (/period|date/.test(reason)) return "The source dates do not cover the same completed month.";
  if (/duplicate|identity|hierarchy/.test(reason)) return "Some source entries cannot be identified safely without a closer check.";
  if (/mismatch|amount|total/.test(reason)) return "Some detailed amounts do not match the source totals.";
  if (/class|location|dimension/.test(reason)) return "Some class or location details are missing from the source report.";
  if (/missing/.test(reason)) return "Some required source detail is missing.";
  return "The report contains details this check cannot verify yet.";
}
function dimension(value: EvidenceDimension): string {
  if (value.state === "present" && value.id) return `ID ${value.id}`;
  if (value.state === "unassigned") return "Not assigned";
  if (value.state === "not_configured") return "Not used in these books";
  if (value.state === "missing_id") return "Name provided without an ID";
  return "Not provided";
}

export function AccountingExpenseEvidenceCard({ data, loading, failed, onRetry, canEdit = false }: {
  data?: AccountingExpenseEvidenceView;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  canEdit?: boolean;
}): React.ReactElement {
  const [visibleGroups, setVisibleGroups] = useState(10);
  const status = expenseEvidenceStatus(data);
  const source = data?.source;
  const groups = data?.groups ?? [];
  const reasons = [...new Set(source?.reasons.map(sourceReason) ?? [])];
  return <section data-accounting-expense-evidence="true" style={styles.card}>
    <h2 style={{ margin: "0 0 6px", fontSize: 17 }}>QuickBooks expense details</h2>
    <p style={styles.copy}>See the source entries behind each expense account.{canEdit ? " Open an account below to review its treatment." : " This panel is read-only."}</p>
    {loading && <p role="status" style={styles.copy}>Loading saved expense details…</p>}
    {failed && <div role="alert" style={styles.warning}>
      <strong>We couldn't refresh the expense details.</strong>
      <div>Any saved detail below may be out of date. This is not a completed source check.</div>
      <button type="button" style={styles.linkButton} onClick={onRetry}>Try again</button>
    </div>}
    {!loading && !failed && <div role="status" style={{ ...styles.status, borderColor: status.checked ? "var(--good)" : "var(--c2)" }}>
      <strong>{status.title}</strong><div>{status.detail}</div>
    </div>}
    {source && <>
      <div style={{ ...styles.copy, marginTop: 12 }}>
        <strong>{source.companyName}</strong> · {dateLabel(source.periodStart)} to {dateLabel(source.periodEnd)} · {source.currency}
      </div>
      <p style={styles.small}>
        {source.reportBasis === "accrual" ? "Expenses are counted when recorded, not when cash is paid." : "The source uses a different expense timing basis."}
        {" "}Showing one saved month, not the dashboard’s selected dates.
      </p>
      <p style={styles.small}>Saved {new Date(source.fetchedAt).toLocaleString("en-US", { timeZone: "UTC", timeZoneName: "short" })}.</p>
      {reasons.length > 0 && <ul style={{ ...styles.copy, paddingLeft: 20 }}>{reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>}
      <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
        {groups.slice(0, visibleGroups).map((group) => <ExpenseGroup key={`${source.snapshotId}:${group.id}`} group={group} currency={source.currency} current={!failed && data?.status === "ready"} snapshotId={source.snapshotId} canEdit={canEdit} />)}
      </div>
      {groups.length === 0 && <p style={styles.copy}>{data?.status === "ready"
        ? "Expense accounts will appear here after QuickBooks reports activity in a completed month."
        : "No expense account detail is included in this saved month."}</p>}
      {visibleGroups < groups.length && <button type="button" style={styles.linkButton} onClick={() => setVisibleGroups((count) => count + 10)}>
        Show more accounts ({Math.min(visibleGroups, groups.length)} of {groups.length} shown)
      </button>}
      <p style={styles.small}>Only the financial details needed for this check are shown. Entry references belong to this saved report and may change after a refresh.</p>
    </>}
    <p style={{ ...styles.copy, fontWeight: 650, marginTop: 14 }}>Matching source totals is not approval to use them in profit. Expense treatments still need a separate review.</p>
    {!loading && (!status.checked || failed) && <Link to="/connections" style={styles.linkButton}>Open Connections →</Link>}
  </section>;
}

function ExpenseGroup({ group, currency, current, snapshotId, canEdit }: { group: ExpenseEvidenceGroup; currency: string; current: boolean; snapshotId: string; canEdit: boolean }): React.ReactElement {
  const [visibleLines, setVisibleLines] = useState(25);
  return <details style={styles.group}>
    <summary style={{ cursor: "pointer", fontSize: 13, lineHeight: 1.6 }}>
      <strong>{group.name}</strong> · {group.lineCount} source {group.lineCount === 1 ? "entry" : "entries"} · {current && group.reconciled ? "Source matched" : "Not cleared"}
      <span style={{ display: "block", ...styles.small }}>Expense report: {money(group.amount, currency)} · Detail total: {money(group.postingAmount, currency)} · Difference: {money(group.difference, currency)}</span>
      {(group.reviewedAllocations?.length ?? 0) > 0 && <span style={{ display: "block", ...styles.small }}>Saved treatment: {group.reviewedAllocations?.map((item) => `${classificationLabel(item.classification)} (${money(item.amount, currency)})`).join(", ")}</span>}
    </summary>
    <div style={{ overflowX: "auto", marginTop: 10 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead><tr>{["Date", "Entry type", "Amount", "Class", "Location", "Transaction reference"].map((label) => <th key={label} scope="col" style={styles.cell}>{label}</th>)}</tr></thead>
        <tbody>{group.lines.slice(0, visibleLines).map((line) => <tr key={line.evidenceId}>
          <td style={styles.cell}>{dateLabel(line.transactionDate)}</td>
          <td style={styles.cell}>{line.transactionType}</td>
          <td style={{ ...styles.cell, whiteSpace: "nowrap" }}>{money(line.amount, currency)}</td>
          <td style={styles.cell}>{dimension(line.class)}</td>
          <td style={styles.cell}>{dimension(line.location)}</td>
          <td style={{ ...styles.cell, overflowWrap: "anywhere" }}>{line.transactionId}</td>
        </tr>)}</tbody>
      </table>
    </div>
    {visibleLines < group.lines.length && <button type="button" style={styles.linkButton} onClick={() => setVisibleLines((count) => count + 25)}>Show more entries ({visibleLines} of {group.lines.length} shown)</button>}
    {canEdit && current && group.reconciled && <ExpenseTreatmentQuestion groupId={group.id} snapshotId={snapshotId} />}
  </details>;
}

const styles: Record<string, React.CSSProperties> = {
  card: { marginTop: 20, padding: 18, borderRadius: "var(--r-sm)", border: "1px solid var(--border)", background: "var(--surface)" },
  copy: { margin: "6px 0", color: "var(--body)", fontSize: 13, lineHeight: 1.5 },
  small: { margin: "5px 0", color: "var(--faint)", fontSize: 11.5, lineHeight: 1.5 },
  status: { borderLeft: "3px solid var(--c2)", padding: "8px 12px", marginTop: 12, color: "var(--body)", fontSize: 12.5, lineHeight: 1.5, background: "var(--surface-2)" },
  warning: { borderLeft: "3px solid var(--bad)", padding: "8px 12px", marginTop: 12, color: "var(--body)", fontSize: 12.5, lineHeight: 1.5 },
  group: { border: "1px solid var(--border)", borderRadius: "var(--r-sm)", padding: "10px 12px" },
  cell: { textAlign: "left", padding: "7px 8px", borderBottom: "1px solid var(--border)", fontWeight: 400, verticalAlign: "top" },
  linkButton: { display: "inline-block", color: "var(--accent)", font: "inherit", fontSize: 12.5, fontWeight: 650, textDecoration: "none", padding: "6px 0", border: 0, background: "none", cursor: "pointer" },
};
